/**
 * Betriebe (Geschäftsfelder) — Zentrale Konfiguration
 * 8 Betriebe: Gutachten, Autoglas, Mietwagen, Werkstatt, Protex, Unfallgiganten, Airbnb, Recht
 *
 * DESIGN: Elegantes, subtiles Farbkonzept pro Betrieb (Linear.app / HubSpot Style).
 * Jeder Betrieb hat eine eigene Farbpalette mit Gradient-Paar.
 * Farben werden dezent eingesetzt: Badges, schmale Balken, Gradient-Akzente.
 * Koop-Dunkelrot (#9B1C1C) bleibt globaler Akzent für Buttons/Actions.
 */

export type BetriebId = 'gutachten' | 'autoglas' | 'mietwagen' | 'werkstatt' | 'protex' | 'unfallgiganten' | 'unfallwiki' | 'airbnb' | 'recht' | 'app_programmierung';

export interface BetriebColor {
  /** Dunkle Farbe (Gradient-Start) */
  from: string;
  /** Helle Farbe (Gradient-Ende) */
  to: string;
  /** Badge-Hintergrund (10% Opacity-Effekt) */
  bgLight: string;
  /** Badge-Text */
  text: string;
  /** Volle Farbe für Punkte, Balken */
  accent: string;
}

export interface Betrieb {
  id: BetriebId;
  name: string;
  subtitle: string;
  firma: string;
  /** Kürzel für kompakte Darstellung */
  kuerzel: string;
  heroImage: string;
  /** Ob dieser Betrieb bereits Akten/Daten hat */
  hasData: boolean;
  /** Elegante Farbpalette */
  color: BetriebColor;
  /** Nur für Admin sichtbar (z.B. Unfallgiganten, Protex, APP Programmierung) */
  adminOnly?: boolean;
}

export const BETRIEBE: Record<BetriebId, Betrieb> = {
  gutachten: {
    id: 'gutachten',
    name: 'Kfz Gutachten',
    subtitle: 'Schadensbegutachtung & Bewertung',
    firma: 'SV Büro Koop',
    kuerzel: 'GA',
    heroImage: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030749412/inzzz3uGAPafuC6RPWHdiR/hero-gutachten-DLQnCZ2MMzocausMaH4ptm.webp',
    hasData: true,
    color: {
      from: '#1E3A5F',
      to: '#2563EB',
      bgLight: 'rgba(37,99,235,0.08)',
      text: '#1E3A5F',
      accent: '#2563EB',
    },
  },
  autoglas: {
    id: 'autoglas',
    name: 'Autoglas',
    subtitle: 'Scheibentausch & Reparatur',
    firma: 'LCS GmbH / Lenneglas',
    kuerzel: 'AG',
    heroImage: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030749412/inzzz3uGAPafuC6RPWHdiR/hero-autoglas-cRxatUrYCyadPCQgc7R59T.webp',
    hasData: true,
    color: {
      from: '#065F46',
      to: '#059669',
      bgLight: 'rgba(5,150,105,0.08)',
      text: '#065F46',
      accent: '#059669',
    },
  },
  mietwagen: {
    id: 'mietwagen',
    name: 'Mietwagen',
    subtitle: 'Unfallersatzfahrzeuge',
    firma: 'LCS GmbH / MietGo',
    kuerzel: 'MW',
    heroImage: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030749412/inzzz3uGAPafuC6RPWHdiR/hero-mietwagen-gyKj5LNRd8hrQSeJrVBT2U.webp',
    hasData: true,
    color: {
      from: '#92400E',
      to: '#D97706',
      bgLight: 'rgba(217,119,6,0.08)',
      text: '#92400E',
      accent: '#D97706',
    },
  },
  werkstatt: {
    id: 'werkstatt',
    name: 'Werkstatt',
    subtitle: 'Demontage & Reparatur',
    firma: 'LCS GmbH',
    kuerzel: 'WK',
    heroImage: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030749412/inzzz3uGAPafuC6RPWHdiR/hero-werkstatt-5mmiUGNbyAE4GxhQDsbSX4.webp',
    hasData: true,
    color: {
      from: '#7F1D1D',
      to: '#DC2626',
      bgLight: 'rgba(220,38,38,0.08)',
      text: '#7F1D1D',
      accent: '#DC2626',
    },
  },
  protex: {
    id: 'protex',
    name: 'Protex / Amazon',
    adminOnly: true,
    subtitle: 'Produktverkauf & Amazon-Handel',
    firma: 'Protex',
    kuerzel: 'PX',
    heroImage: '',
    hasData: false,
    color: {
      from: '#4C1D95',
      to: '#7C3AED',
      bgLight: 'rgba(124,58,237,0.08)',
      text: '#4C1D95',
      accent: '#7C3AED',
    },
  },
  unfallgiganten: {
    id: 'unfallgiganten',
    name: 'Unfallgiganten',
    adminOnly: true,
    subtitle: 'Online-Plattform',
    firma: 'Unfallgiganten',
    kuerzel: 'UG',
    heroImage: '',
    hasData: false,
    color: {
      from: '#134E4A',
      to: '#0D9488',
      bgLight: 'rgba(13,148,136,0.08)',
      text: '#134E4A',
      accent: '#0D9488',
    },
  },
  airbnb: {
    id: 'airbnb',
    name: 'Airbnb / Immobilien',
    adminOnly: true,
    subtitle: 'Vermietung Glatzer Str. 8',
    firma: 'Immobilien Koop',
    kuerzel: 'AI',
    heroImage: '',
    hasData: false,
    color: {
      from: '#78350F',
      to: '#B45309',
      bgLight: 'rgba(180,83,9,0.08)',
      text: '#78350F',
      accent: '#B45309',
    },
  },
  recht: {
    id: 'recht',
    name: 'Recht & Forderungen',
    subtitle: 'Rechtsakten & Forderungsmanagement',
    firma: 'SV Büro Koop / LCS GmbH',
    kuerzel: 'RA',
    heroImage: '',
    hasData: false,
    adminOnly: true,
    color: {
      from: '#1E3A5F',
      to: '#1D4ED8',
      bgLight: 'rgba(30,58,95,0.08)',
      text: '#1E3A5F',
      accent: '#1E3A5F',
    },
  },
  unfallwiki: {
    id: 'unfallwiki',
    name: 'Unfallwiki',
    subtitle: 'Wissensdatenbank Unfallrecht',
    firma: 'Unfallgiganten',
    kuerzel: 'UW',
    heroImage: '',
    hasData: false,
    adminOnly: true,
    color: {
      from: '#0C4A6E',
      to: '#0284C7',
      bgLight: 'rgba(2,132,199,0.08)',
      text: '#0C4A6E',
      accent: '#0284C7',
    },
  },
  app_programmierung: {
    id: 'app_programmierung',
    name: 'APP Programmierung',
    subtitle: 'Entwicklung & Automatisierung',
    firma: 'Intern',
    kuerzel: 'DEV',
    heroImage: '',
    hasData: false,
    adminOnly: true,
    color: {
      from: '#374151',
      to: '#6B7280',
      bgLight: 'rgba(107,114,128,0.08)',
      text: '#374151',
      accent: '#6B7280',
    },
  },
};

export const BETRIEB_LIST = Object.values(BETRIEBE);
export const BETRIEB_IDS = Object.keys(BETRIEBE) as BetriebId[];
export const DEFAULT_BETRIEB: BetriebId = 'gutachten';

/** Hilfsfunktion: Betrieb-Farbe nach business_unit string */
export function getBetriebColor(businessUnit: string): BetriebColor {
  const betrieb = BETRIEBE[businessUnit as BetriebId];
  return betrieb?.color || BETRIEBE.gutachten.color;
}

/**
 * Funktionsstatus-System
 * Jede Funktion/Seite/Feature hat einen Status:
 * - 'aktiv'          → ✓ aktiv (funktioniert bereits)
 * - 'in_entwicklung' → ⚙ in Entwicklung (wird gerade gebaut)
 * - 'geplant'        → ○ geplant (noch nicht gebaut, auf Roadmap)
 */
export type FeatureStatus = 'aktiv' | 'in_entwicklung' | 'geplant';

export interface FeatureStatusInfo {
  status: FeatureStatus;
  label: string;
  icon: string;
  colorClass: string;
}

/** Reduzierte Farben: nur dezentes Grau/Rot */
export const FEATURE_STATUS: Record<FeatureStatus, FeatureStatusInfo> = {
  aktiv: {
    status: 'aktiv',
    label: '✓ aktiv',
    icon: '✓',
    colorClass: 'text-gray-500',
  },
  in_entwicklung: {
    status: 'in_entwicklung',
    label: '⚙ in Entwicklung',
    icon: '⚙',
    colorClass: 'text-gray-400',
  },
  geplant: {
    status: 'geplant',
    label: '○ geplant',
    icon: '○',
    colorClass: 'text-gray-300',
  },
};

/** Hilfsfunktion: StatusBadge-Klassen — dezent, einheitlich grau */
export function getFeatureStatusBadge(status: FeatureStatus): string {
  switch (status) {
    case 'aktiv': return 'bg-gray-50 text-gray-500 border-gray-200';
    case 'in_entwicklung': return 'bg-gray-50 text-gray-400 border-gray-200';
    case 'geplant': return 'bg-gray-50 text-gray-300 border-gray-200';
  }
}
